import React, { useState, useRef, useEffect } from 'react';
import { Lock, RefreshCw } from 'lucide-react';

interface OtpModalProps {
  email: string;
  password: string;
  onVerify: (otp: string) => void;
  onResend: () => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string;
}

const OtpModal: React.FC<OtpModalProps> = ({ email, password, onVerify, onResend, onCancel, loading, error }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
    
    // Start countdown
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedData = value.slice(0, 6).split('');
      const newOtp = [...otp];
      pastedData.forEach((char, i) => {
        if (index + i < 6 && /^\d$/.test(char)) {
          newOtp[index + i] = char;
        }
      });
      setOtp(newOtp);
      
      // Focus last filled input or next empty
      const nextIndex = Math.min(index + pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    if (/^\d$/.test(value) || value === '') {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter' && otp.every(digit => digit !== '')) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    const otpString = otp.join('');
    if (otpString.length === 6) {
      onVerify(otpString);
    }
  };

  const handleResendClick = () => {
    if (canResend) {
      onResend();
      setCanResend(false);
      setResendCooldown(60);
      
      // Restart countdown
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '450px',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#DBEAFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}
          >
            <Lock size={32} style={{ color: '#3B82F6' }} />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Enter OTP Code
          </h2>
          <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>
            We've sent a 6-digit code to
            <br />
            <strong>{email}</strong>
          </p>
        </div>

        {/* OTP Input */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              style={{
                width: '3rem',
                height: '3.5rem',
                border: '2px solid #D1D5DB',
                borderRadius: '8px',
                textAlign: 'center',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#333',
                backgroundColor: '#fff',
                outline: 'none',
                transition: 'border-color 0.2s',
                ...(digit && { borderColor: '#3B82F6' })
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3B82F6';
                e.target.select();
              }}
              onBlur={(e) => {
                if (!digit) {
                  e.target.style.borderColor = '#D1D5DB';
                }
              }}
            />
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              backgroundColor: '#FEE2E2',
              color: '#991B1B',
              padding: '0.75rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              textAlign: 'center'
            }}
          >
            {error}
          </div>
        )}

        {/* Resend OTP */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          {canResend ? (
            <button
              onClick={handleResendClick}
              style={{
                background: 'none',
                border: 'none',
                color: '#3B82F6',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                justifyContent: 'center',
                margin: '0 auto'
              }}
            >
              <RefreshCw size={16} />
              Resend OTP
            </button>
          ) : (
            <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>
              Resend OTP in <strong>{resendCooldown}s</strong>
            </p>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1,
              padding: '0.75rem',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || otp.some(digit => !digit)}
            style={{
              flex: 1,
              padding: '0.75rem',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: loading || otp.some(digit => !digit) ? 'not-allowed' : 'pointer',
              opacity: loading || otp.some(digit => !digit) ? 0.5 : 1
            }}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>

        {/* Expires in 10 minutes notice */}
        <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '0.75rem', marginTop: '1rem' }}>
          ⏱ This OTP will expire in 10 minutes
        </p>
      </div>
    </div>
  );
};

export default OtpModal;

